/**
 * NgwEditorAdapter — `IEditor` / `IMailEditor` 의 NgwEditor 구현.
 *
 * 본 구현 sub-adapter (PR-N1 + PR-N2 + PR-N3 + PR-N4 누적):
 *   - `lifecycle`: `NgwEditorLifecycle` (React root 마운트 + `<Editor />` 렌더 + onReady 폴링 +
 *                  PR-N3/N4 의 어댑터 자체 extension + nodeViews 주입)
 *   - `content`:   `NgwEditorContent` (setHTML/getHTML/insertText/insertHTML/setDefaultStyle 등)
 *   - `state`:     `NgwEditorState` (dirty 플래그 / noInput 타이머 / link feature 위임)
 *   - `cursor`:    `NgwEditorCursor` (PR-N2 — ProseMirror Selection API 기반)
 *   - `image`:     `NgwEditorImage` (PR-N2 — blob URL Map + findNodes/canvas drawImage)
 *   - `theme`:     `NgwEditorTheme` (PR-N2 — chroma-js luminance + className 토글)
 *   - `direction`: `NgwEditorDirection` (PR-N2 — DOM dir attr / spellcheck property)
 *   - `view`:      `NgwEditorView` (PR-N2 — inline style 직접 조작)
 *   - `command`:   `NgwEditorCommand` (PR-N3 — NGW_COMMAND_MAP + 특수 분기 + `removeFormat`/
 *                  `directionLTR`/`directionRTL`/`codeView` 의 어댑터 자체 처리)
 *   - `mail`:      `NgwEditorMailExtensions` (PR-N4 — 4 region (nBody/signature/quotedHeader/
 *                  quotedBody) + quotedButton + cidMap/acex/nBodyCounter 클래스 멤버. insert 는
 *                  PR-N5 까지 stub. `mailRegionsExtension` + `quotedBodyNodeView` 는 lifecycle
 *                  에서 주입). `IMailEditor` 표면 충족 — `editor.mail.<...>` 비-null assertion 불요.
 *
 * `toolbar` 는 NgwEditor 가 내장하므로 영구적으로 `undefined` (05 §4.10, pr-n0-inventory §6.2).
 *
 * **DOM context 는 항상 host 경유**: parent `document.*` 직접 접근 금지. 모든 DOM 접근은
 * `this.host.getDocument()` / `getWindow()` 또는 `lifecycle.getHandle().getEditorDOM()` 을
 * 통과한다.
 */

import type { IEditor, IMailEditor } from '../IEditor'
import type { IEditorEventHandler } from '../IEditorEventHandler'
import { InlineHost } from './InlineHost'
import { NgwEditorCommand } from './NgwEditorCommand'
import { NgwEditorContent } from './NgwEditorContent'
import { NgwEditorCursor } from './NgwEditorCursor'
import { NgwEditorDirection } from './NgwEditorDirection'
import { NgwEditorImage } from './NgwEditorImage'
import { NgwEditorLifecycle } from './NgwEditorLifecycle'
import { NgwEditorMailExtensions } from './NgwEditorMailExtensions'
import { NgwEditorState } from './NgwEditorState'
import { NgwEditorTheme } from './NgwEditorTheme'
import { NgwEditorView } from './NgwEditorView'

export class NgwEditorAdapter implements IEditor, IMailEditor {
  readonly lifecycle: NgwEditorLifecycle
  readonly content: NgwEditorContent
  readonly state: NgwEditorState
  readonly cursor: NgwEditorCursor
  readonly image: NgwEditorImage
  readonly theme: NgwEditorTheme
  readonly direction: NgwEditorDirection
  readonly view: NgwEditorView
  readonly command: NgwEditorCommand

  /** NgwEditor 가 toolbar 를 자체 렌더하므로 영구적으로 미주입. */
  readonly toolbar: undefined = undefined

  /**
   * PR-N4 — 메일 도메인 표면 본 구현.
   *
   * `IMailEditor` 의 `mail` 은 non-optional. EmailForm / SignatureEdit 호출부의 `editor.mail.<...>`
   * 비-null assertion 이 불필요해진다.
   */
  readonly mail: NgwEditorMailExtensions

  /** host 는 lifecycle.init 이후에 설정됨. */
  private _host: InlineHost | null = null
  private _handler: IEditorEventHandler | null = null
  private _dirty = false

  constructor() {
    this.lifecycle = new NgwEditorLifecycle(this)
    this.content = new NgwEditorContent(this)
    this.state = new NgwEditorState(this)
    this.cursor = new NgwEditorCursor(this)
    this.image = new NgwEditorImage(this)
    this.theme = new NgwEditorTheme(this)
    this.direction = new NgwEditorDirection(this)
    this.view = new NgwEditorView(this)
    this.command = new NgwEditorCommand(this)
    this.mail = new NgwEditorMailExtensions(this)
  }

  /**
   * `IEditorHost` 노출. lifecycle.init 이후에 사용 가능.
   */
  get host(): InlineHost {
    if (this._host == null) {
      throw new Error('NgwEditorAdapter: host not initialized (call lifecycle.init first)')
    }
    return this._host
  }

  /**
   * lifecycle 내부에서 host 가 아직 설정 안 됐을 가능성을 안고 호출하는 경로용. throw 대신 null
   * 반환 — onReady 콜백에서 host 가 일시적으로 null 인 race 회피.
   * @internal
   */
  getHostInternal(): InlineHost | null {
    return this._host
  }

  /** `lifecycle.init` 에서 host 설정. @internal */
  setHost(host: InlineHost): void {
    this._host = host
  }

  /** `lifecycle.dispose` 에서 host 정리. @internal */
  clearHost(): void {
    this._host = null
  }

  /** 핸들러 접근. @internal */
  getHandler(): IEditorEventHandler | null {
    return this._handler
  }

  /** `lifecycle.setEventHandler` 에서 호출. @internal */
  setHandlerInternal(handler: IEditorEventHandler | null): void {
    this._handler = handler
  }

  /** dirty 플래그 set. lifecycle.onChange 가 호출. @internal */
  markDirty(): void {
    this._dirty = true
  }

  /** dirty 플래그 clear. state.resetChangedState 가 호출. @internal */
  clearDirty(): void {
    this._dirty = false
  }

  /** dirty 플래그 조회. content.isContentModified 가 사용. @internal */
  isDirty(): boolean {
    return this._dirty
  }
}
